import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../shared/ui/Toast/ToastProvider';
import { docsApi } from '../../docs/api';
import { useUpdateDoc } from '../../docs/hooks/useDocsData';
import { tasksApi } from '../api';

// Compartir-por-mención del documento estructurado de una tarea del Cuadro de Trabajo
// (integración main→dev, epic D1.4). Al mencionar (@) a alguien sin acceso al documento
// vinculado se abre `WorkframeSharePromptModal`; conceder acceso reescribe el blob
// `doc_permissions` completo desde el navegador (puerto directo — decisión del usuario
// 2026-09-09, D1-workframe.md §0.4.4: el hueco de seguridad se acepta; un caso de uso
// server-side es una fase aparte). La escritura va por `useUpdateDoc`, no por `apiClient` crudo.

function parsePerms(raw) {
    if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch { return null; }
    }
    return raw || null;
}

export function useWorkframeDocSharing({ activeTask }) {
    const { t } = useTranslation('tasks');
    const toast = useToast();
    const updateDoc = useUpdateDoc();

    const [sharePrompt, setSharePrompt] = useState(null); // { targetUser, docId, docTitle }
    const [isGranting, setIsGranting] = useState(false);
    const resolvedDocIdRef = useRef({ taskId: null, docId: null });

    const resolveDocId = useCallback(async () => {
        if (!activeTask) return null;
        if (activeTask.doc_id) return activeTask.doc_id;
        if (resolvedDocIdRef.current.taskId === activeTask.id) return resolvedDocIdRef.current.docId;
        if (!activeTask.workframe_id) return null;
        try {
            const res = await tasksApi.fetchWorkframeById(activeTask.workframe_id);
            const docId = res?.success ? res.workframe?.doc_id || null : null;
            resolvedDocIdRef.current = { taskId: activeTask.id, docId };
            return docId;
        } catch {
            return null;
        }
    }, [activeTask]);

    const onMentionSelect = useCallback(async (mentionProps) => {
        if (!mentionProps?.id) return;
        const docId = await resolveDocId();
        if (!docId) return;
        try {
            const res = await docsApi.getById(docId);
            if (!res?.success || !res.doc) return;
            const doc = res.doc;
            const perms = parsePerms(doc.doc_permissions);
            const members = Array.isArray(perms?.members) ? perms.members : [];
            const isOwner = String(doc.created_by) === String(mentionProps.id) || String(doc.autor) === String(mentionProps.id);
            const isMember = members.some((m) => String(m.id) === String(mentionProps.id) && m.access?.view !== false);
            const isAnyone = !!perms?.anyoneAccess?.view;
            if (!isOwner && !isMember && !isAnyone) {
                setSharePrompt({
                    targetUser: mentionProps,
                    docId,
                    docTitle: doc.title || t('workframe.share.linkedDocFallback'),
                });
            }
        } catch (err) {
            console.error('[useWorkframeDocSharing] mention access check failed:', err);
        }
    }, [resolveDocId, t]);

    const onGrantAccess = useCallback(async ({ view, edit }) => {
        if (!sharePrompt?.docId || !sharePrompt.targetUser) return;
        setIsGranting(true);
        try {
            const res = await docsApi.getById(sharePrompt.docId);
            if (!res?.success || !res.doc) return;
            const perms = parsePerms(res.doc.doc_permissions) || {};
            const members = Array.isArray(perms.members) ? [...perms.members] : [];
            const target = sharePrompt.targetUser;
            const targetName = target.nick || target.name || t('workframe.bitacora.user');
            const newMember = {
                id: target.id,
                name: targetName,
                nick: targetName,
                avatar: target.avatar || null,
                access: { view: Boolean(view), edit: Boolean(edit), time: null },
                lastSeen: new Date().toISOString(),
            };
            const idx = members.findIndex((m) => String(m.id) === String(target.id));
            if (idx >= 0) members[idx] = newMember;
            else members.push(newMember);
            await updateDoc.mutateAsync({ id: sharePrompt.docId, payload: { doc_permissions: { ...perms, members } } });
            toast.success(t('workframe.share.granted', { name: targetName }));
            setSharePrompt(null);
        } catch (err) {
            console.error('[useWorkframeDocSharing] grant failed:', err);
            toast.error(t('workframe.share.grantFailed'));
        } finally {
            setIsGranting(false);
        }
    }, [sharePrompt, updateDoc, toast, t]);

    return {
        onMentionSelect,
        modalProps: {
            isOpen: !!sharePrompt,
            onClose: () => setSharePrompt(null),
            targetUser: sharePrompt?.targetUser || null,
            docTitle: sharePrompt?.docTitle || '',
            onGrantAccess,
            isSubmitting: isGranting,
        },
    };
}
